import { useMemo } from "react";
import { useAuth } from "react-oidc-context";
import { config } from "../../../config";

/**
 * Cliente de la gestión de usuarios del ERP (endpoints `/api/v1/users` del backend
 * PR #140). Está escrito a mano —tipos incluidos— a propósito: el contrato aún no
 * está mergeado, así que todavía no vive en el cliente generado `@farmacenter/api`.
 * En cuanto el contrato aterrice, esta pantalla migra al cliente generado y este
 * módulo desaparece. Reutiliza el token vigente de la sesión, igual que el
 * ApiProvider.
 */

export interface Usuario {
  id: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  documentId: string | null;
  enabled: boolean;
  /** Clave del preset ("cajero", …), o null si aún no tiene rol reconocido. */
  preset: string | null;
  presetLabel: string | null;
  branchIds: string[];
  createdAt: string | null;
}

export interface Preset {
  key: string;
  roleName: string;
  label: string;
  description: string;
  scopes: string[];
}

export interface CrearUsuario {
  username: string;
  firstName: string;
  lastName?: string;
  email?: string;
  documentId?: string;
  preset: string;
  enabled: boolean;
  temporaryPassword: string;
  branchIds?: string[];
}

export interface ActualizarUsuario {
  firstName: string;
  lastName?: string;
  email?: string;
  documentId?: string;
  preset: string;
  enabled: boolean;
  branchIds?: string[];
}

export interface Rol {
  key: string;
  roleName: string;
  label: string;
  description: string | null;
  isProtected: boolean;
  scopes: string[];
}

export interface GuardarRol {
  label: string;
  description?: string;
  scopes: string[];
}

export interface UsuariosApi {
  listar: (search?: string) => Promise<Usuario[]>;
  obtener: (userId: string) => Promise<Usuario>;
  crear: (body: CrearUsuario) => Promise<Usuario>;
  actualizar: (userId: string, body: ActualizarUsuario) => Promise<Usuario>;
  eliminar: (userId: string) => Promise<void>;
  reiniciarClave: (userId: string, temporaryPassword: string, temporary: boolean) => Promise<void>;
  presets: () => Promise<Preset[]>;
  /** Catálogo de roles editables. */
  listarRoles: () => Promise<Rol[]>;
  crearRol: (key: string, body: GuardarRol) => Promise<Rol>;
  actualizarRol: (key: string, body: GuardarRol) => Promise<Rol>;
  eliminarRol: (key: string) => Promise<void>;
  /** Todos los permisos (scopes) que un rol puede otorgar. */
  listarScopes: () => Promise<string[]>;
}

/** Extrae un mensaje legible de una respuesta de error (ProblemDetail del backend). */
async function leerError(res: Response): Promise<Error> {
  try {
    const cuerpo = (await res.json()) as { detail?: string; title?: string };
    return new Error(cuerpo.detail ?? cuerpo.title ?? `Error ${res.status}`);
  } catch {
    return new Error(`Error ${res.status}`);
  }
}

export function useUsuariosApi(): UsuariosApi {
  const auth = useAuth();

  return useMemo<UsuariosApi>(() => {
    async function pedir<T>(metodo: string, ruta: string, cuerpo?: unknown): Promise<T> {
      const token = auth.user?.access_token ?? null;
      const res = await fetch(`${config.apiBaseUrl}${ruta}`, {
        method: metodo,
        headers: {
          ...(cuerpo !== undefined ? { "Content-Type": "application/json" } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        ...(cuerpo !== undefined ? { body: JSON.stringify(cuerpo) } : {}),
      });
      if (!res.ok) {
        throw await leerError(res);
      }
      if (res.status === 204) {
        return undefined as T;
      }
      return (await res.json()) as T;
    }

    return {
      listar: (search) =>
        pedir<Usuario[]>(
          "GET",
          `/api/v1/users${search && search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ""}`,
        ),
      obtener: (userId) => pedir<Usuario>("GET", `/api/v1/users/${encodeURIComponent(userId)}`),
      crear: (body) => pedir<Usuario>("POST", "/api/v1/users", body),
      actualizar: (userId, body) =>
        pedir<Usuario>("PUT", `/api/v1/users/${encodeURIComponent(userId)}`, body),
      eliminar: (userId) => pedir<void>("DELETE", `/api/v1/users/${encodeURIComponent(userId)}`),
      reiniciarClave: (userId, temporaryPassword, temporary) =>
        pedir<void>("POST", `/api/v1/users/${encodeURIComponent(userId)}/password-reset`, {
          temporaryPassword,
          temporary,
        }),
      presets: () => pedir<Preset[]>("GET", "/api/v1/users/presets"),
      listarRoles: () => pedir<Rol[]>("GET", "/api/v1/users/roles"),
      crearRol: (key, body) => pedir<Rol>("POST", "/api/v1/users/roles", { key, ...body }),
      actualizarRol: (key, body) =>
        pedir<Rol>("PUT", `/api/v1/users/roles/${encodeURIComponent(key)}`, body),
      eliminarRol: (key) => pedir<void>("DELETE", `/api/v1/users/roles/${encodeURIComponent(key)}`),
      listarScopes: () => pedir<string[]>("GET", "/api/v1/users/scopes"),
    };
  }, [auth]);
}
